import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import CssBaseline from '@mui/material/CssBaseline'
import { Container, Typography, useMediaQuery, useTheme, Button, Stack } from '@mui/material'
import { Outlet, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { changeCurrentLanguage } from '../../store/actions'
import { ReactComponent as InesLogo } from '../../assets/logos/Suite.svg'
import { ReactComponent as InesLogoDark } from '../../assets/logos/Suite_dark.svg'
import { ReactComponent as LanguageLogo } from '../../assets/walkthrough/language.svg'
import { updateProfile } from '../../store/actions/user/thunk'
import FsAppsMenu from './AppsMenu'
import FsAccountMenu from './AccountMenu'

const HomeDrawer = () => {
  const { token, account, currentLanguage, currentTheme } = useAppSelector(
    (state) => state.currentUser,
  )
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const theme = useTheme()
  const largerThanMid = useMediaQuery(theme.breakpoints.up('sm'))

  const switchLanguage = (l: 'de' | 'en') => {
    i18n.changeLanguage(l)
    document.documentElement.setAttribute('lang', l)
    dispatch(changeCurrentLanguage(l))
  }

  useEffect(() => {
    i18n.changeLanguage(currentLanguage)
    document.documentElement.setAttribute('lang', currentLanguage)
  }, [])

  return (
    <div style={{ backgroundColor: currentTheme === 'light' ? theme.palette.grey[50] : '' }}>
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            zIndex: 1600,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            margin: 0.5,
            width: 'calc(100% - 8px)',
          }}
          elevation={3}
        >
          <Toolbar
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 1600,
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 2,
              }}
            >
              {token && <FsAppsMenu />}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  onClick={() => navigate('/ines/home')}
                >
                  {currentTheme === 'dark' ? (
                    <InesLogoDark height={35} width="auto" />
                  ) : (
                    <InesLogo height={35} width="auto" />
                  )}
                </div>
              </div>
            </div>

            {token && <FsAccountMenu />}
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pt: 2,
            pb: 3,
            height: '100vh',
            width: '100%',
            overflowY: 'hidden',
            zIndex: 10,
          }}
        >
          <Toolbar />
          <Stack>
            <Container
              maxWidth="xl"
              sx={{ overflowY: 'auto', height: 'calc(100vh - 89px)', pb: 8 }}
            >
              {largerThanMid ? (
                <Outlet />
              ) : (
                <Box>
                  <Typography variant="h2" textAlign="center">
                    {t('no.small.screen')}
                  </Typography>
                </Box>
              )}
            </Container>
          </Stack>
        </Box>
        {!account?.profile || account?.profile.isFirstTime ? (
          <Box
            height="100%"
            width="100%"
            position="absolute"
            top={0}
            left={0}
            sx={{ backgroundColor: '#00000080', zIndex: 1600 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              py={4}
              width="400px"
              sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <LanguageLogo color={theme.palette.primary.main} />
              <Typography mt={2} mx={4}>
                Wählen Sie Ihre gewünschte Sprache.
              </Typography>
              <Typography mx={4}>Choose your prefered language.</Typography>
              <Button
                variant="contained"
                sx={{
                  mt: 2,
                }}
                onClick={() => {
                  dispatch(
                    updateProfile({
                      profile: { preferredLanguage: 'de', isFirstTime: false },
                    }),
                  )
                  switchLanguage('de')
                }}
              >
                Weiter auf Deutsch
              </Button>
              <Button
                variant="contained"
                sx={{
                  mt: 2,
                }}
                onClick={() => {
                  dispatch(
                    updateProfile({
                      profile: { preferredLanguage: 'en', isFirstTime: false },
                    }),
                  )
                  switchLanguage('en')
                }}
              >
                Continue in English
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    </div>
  )
}

export default HomeDrawer
